import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { LiveScreen, QueryState, StatusPill, formatDate } from '@/components/live';
import { callRpc } from '@/api/resources';
import { toast } from '@/lib/toast';
import { useRows } from '@/hooks/useResource';

interface MessageRow {
  id: string; channel: string; status: string; rendered_preview: string | null;
  sent_at: string | null; read_at: string | null; responded_at: string | null;
  failure_reason: string | null; template_code: string | null; use_case: string | null;
  alert_code: string | null; alert_type: string | null; severity: string | null;
  alert_summary: string | null; plot_id: string | null; acknowledged_at: string | null;
  created_at: string;
}

/**
 * What the platform has told you, and what you have answered.
 *
 * This is deliberately not the communication log: that screen is an administrator's view of every
 * message the organisation sent, and needs a permission. This one shows the rows addressed to the
 * signed-in user and nothing else, which is why it needs no permission at all.
 *
 * Acknowledging is the point of the screen. A satellite alert nobody has answered is a change
 * nobody has taken responsibility for, so the button says what it means: not "dismiss", which
 * would make the row go away, but "I have this", which puts the reader's name on it.
 */
export default function MyAlertsScreen() {
  const client = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);

  const messages = useRows<MessageRow>('my_messages', {
    orderBy: { column: 'created_at', ascending: false }, limit: 100,
  });

  const acknowledge = useMutation({
    mutationFn: (id: string) => callRpc('fn_message_acknowledge', { p_message_event_id: id, p_response: null }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['my_messages'] }),
    onError: (error: Error) => toast.error(error.message || 'That could not be recorded.'),
  });
  const markRead = useMutation({
    mutationFn: (id: string) => callRpc('fn_message_mark_read', { p_message_event_id: id }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['my_messages'] }),
  });

  const rows = messages.data ?? [];
  const unanswered = rows.filter((row) => !row.responded_at);

  function open(row: MessageRow) {
    setOpenId(openId === row.id ? null : row.id);
    if (!row.read_at && openId !== row.id) markRead.mutate(row.id);
  }

  return (
    <LiveScreen
      subtitle={unanswered.length > 0
        ? `What the platform has told you. ${unanswered.length} still waiting for an answer.`
        : 'What the platform has told you. Nothing is waiting for an answer.'}
      title="My alerts"
    >
      <QueryState empty="Nothing has been sent to you yet." query={messages}>
        <ul className="msg-list">
          {rows.map((row) => (
            <li className={`msg${row.responded_at ? ' is-answered' : ''}${row.read_at ? '' : ' is-unread'}`} key={row.id}>
              <button className="msg-head" onClick={() => open(row)} type="button">
                <span className="cell-stack">
                  <span className="msg-title">
                    {row.alert_summary ?? row.template_code ?? 'Message'}
                    {row.alert_code ? <span className="muted"> · {row.alert_code}</span> : null}
                  </span>
                  <small className="muted">
                    {formatDate(row.sent_at ?? row.created_at)} · {row.channel === 'in_app' ? 'in app' : row.channel}
                    {row.failure_reason ? ` · ${row.failure_reason}` : ''}
                  </small>
                </span>
                {row.severity && <StatusPill value={row.severity} />}
                <StatusPill value={row.status} />
              </button>

              {openId === row.id && (
                <div className="msg-body">
                  <p style={{ whiteSpace: 'pre-line' }}>{row.rendered_preview ?? '--'}</p>
                  {row.responded_at ? (
                    <p className="muted">
                      You acknowledged this on {formatDate(row.responded_at)}. The wording you saw is kept with
                      your acknowledgement, so later edits to the template cannot change it.
                    </p>
                  ) : (
                    <button className="btn primary" disabled={acknowledge.isPending}
                            onClick={() => acknowledge.mutate(row.id)} type="button">
                      <Check size={15} /> I have this
                    </button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      </QueryState>
      <p className="muted" style={{ fontSize: '12.5px' }}>
        Acknowledging records that you have seen the alert. It is not a judgement on what the satellite
        saw: that stays open until somebody reviews it against field records.
      </p>
    </LiveScreen>
  );
}
